import { Typography, Box, IconButton, Tooltip, FormHelperText } from '@mui/material';
import { FC, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Close } from '@mui/icons-material';

import { JobseekerCreateDto } from '@libs/dto';
import { useForm } from '@carcareers/contexts';

export const ResumeUploadStep: FC = () => {
  const { data, getErrors, setData } = useForm<JobseekerCreateDto>();
  const resumeFile = data?.resume as File | undefined;
  const errors = getErrors('resume');

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setData('resume', acceptedFiles[0]);
    }
  }, [setData]);

  const removeFile = () => {
    setData('resume', null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    multiple: false,
    maxFiles: 1,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
        Upload Resume
      </Typography>
      
      <Typography variant="body1" color="text.secondary">
        Upload your resume to enhance your profile. This step is optional but recommended.
      </Typography>

      {errors && (
        <FormHelperText sx={{ color: 'error.main' }}>
          {errors}
        </FormHelperText>
      )}

      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? 'action.hover' : 'transparent',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        <Typography>
          {isDragActive 
            ? 'Drop your resume here' 
            : 'Drag and drop your resume here, or click to select a file'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Supported formats: PDF, DOC, DOCX (Max 5MB)
        </Typography>
      </Box>

      {resumeFile && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected resume:
          </Typography>
          <Box
            sx={{
              py: 1,
              pl: 2,
              pr: 1,
              display: 'flex',
              borderRadius: 1,
              alignItems: 'center',
              border: 1,
              borderColor: 'divider',
              backgroundColor: 'grey.50',
            }}
          >
            <Typography sx={{ flex: 1 }}>
              {resumeFile.name.length > 50 ? `${resumeFile.name.substring(0, 50)}...` : resumeFile.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              {(resumeFile.size / (1024 * 1024)).toFixed(2)} MB
            </Typography>
            <Tooltip title="Remove file" placement="top">
              <IconButton size="small" onClick={removeFile}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {resumeFile && (
        <FormHelperText sx={{ color: 'success.main' }}>
          Resume uploaded successfully! You can now continue to the next steps or replace this file.
        </FormHelperText>
      )}
    </Box>
  );
};

export default ResumeUploadStep;
